import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  Container,
  Content,
  Table,
  Button
} from 'rsuite';

import NavigationBar from '../Shared/NavigationBar';
import SetSearchBar from './SetSearchBar';
import { API_BASE } from '../../helpers/authFetch';
import SetFilters from './SetFilters';
import { SCRYFALL_ID_DRAG_TYPE } from '../../helpers/dragDropCardParser';
import { usePreferences } from '../Preferences/PreferencesContext';
import { VIEW_MODES } from '../../helpers/viewModeConstants';

const { Column, HeaderCell, Cell } = Table;

const RARITY_ORDER = {
  'common': 0,
  'uncommon': 1,
  'rare': 2,
  'mythic': 3
};

const SCRYFALL_ASPECT_RATIO = 204 / 146;

const CARD_DISPLAY_SIZES = {
  small:  { width: 220, height: Math.round(220 * SCRYFALL_ASPECT_RATIO) },
  normal: { width: 300, height: Math.round(300 * SCRYFALL_ASPECT_RATIO) },
  large:  { width: 400, height: Math.round(400 * SCRYFALL_ASPECT_RATIO) },
};

const CARD_SCALE = isMobile ? 0.7 : 1;

const SPACING = {
  gridGap: 12,
  gridPadding: 8,
  contentPadding: 16,
  toggleMargin: 16,
  placeholderPadding: 8,
};

const COLORS = {
  cardBorderRadius: 6,
  cardTransitionDuration: '0.2s',
  cardScaleHover: 1.05,
  placeholderBg: '#333',
  placeholderBorder: '#555',
  placeholderText: '#aaa',
  toggleBg: '#3498db',
  toggleIconActive: '#ffffff',
  toggleIconInactive: '#888',
  toggleBgInactive: 'transparent',
};

const FONT = {
  placeholderSize: 11,
};

const MANA_COST_COLUMN_WIDTH = 130;
const COLORS_COLUMN_WIDTH = 70;

const ViewModeToggle = ({ value, onChange }) => {
  const VIEW_MODE_ICONS = {
    [VIEW_MODES.TABLE]: { icon: '≡', label: 'Table view' },
    [VIEW_MODES.VISUAL]: { icon: '⊞', label: 'Grid view' },
  };

  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <Button
        title={VIEW_MODE_ICONS[VIEW_MODES.TABLE].label}
        appearance={value === VIEW_MODES.TABLE ? 'primary' : 'default'}
        onClick={() => onChange(VIEW_MODES.TABLE)}
        size="xs"
        style={{ fontSize: 16, padding: '6px 10px', minWidth: 32, minHeight: 32 }}
      >
        {VIEW_MODE_ICONS[VIEW_MODES.TABLE].icon}
      </Button>
      <Button
        title={VIEW_MODE_ICONS[VIEW_MODES.VISUAL].label}
        appearance={value === VIEW_MODES.VISUAL ? 'primary' : 'default'}
        onClick={() => onChange(VIEW_MODES.VISUAL)}
        size="xs"
        style={{ fontSize: 16, padding: '6px 10px', minWidth: 32, minHeight: 32 }}
      >
        {VIEW_MODE_ICONS[VIEW_MODES.VISUAL].icon}
      </Button>
    </div>
  );
};

const GridView = ({ sortedData, cardDisplaySize, onCardClick }) => {
  const sizeKey = CARD_DISPLAY_SIZES[cardDisplaySize] ? cardDisplaySize : 'normal';
  const cardWidth = Math.round(CARD_DISPLAY_SIZES[sizeKey].width * CARD_SCALE);
  const cardHeight = Math.round(CARD_DISPLAY_SIZES[sizeKey].height * CARD_SCALE);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : `repeat(auto-fill, minmax(${cardWidth}px, 1fr))`,
        gap: `${SPACING.gridGap}px`,
        padding: `${SPACING.gridPadding}px`,
      }}
    >
      {sortedData.map((card) => {
        const imgSrc = card.image_uris?.small || card.card_faces?.[0]?.image_uris?.small;
        return (
          <div
            key={card.id}
            style={{
              cursor: 'pointer',
              borderRadius: `${COLORS.cardBorderRadius}px`,
              overflow: 'hidden',
              transition: `transform ${COLORS.cardTransitionDuration}`,
            }}
            onClick={() => onCardClick(card)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = `scale(${COLORS.cardScaleHover})`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {imgSrc ? (
              <img
                src={imgSrc}
                alt={card.name}
                width={cardWidth}
                height={cardHeight}
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  borderRadius: `${COLORS.cardBorderRadius}px`,
                }}
              />
            ) : (
              <div
                style={{
                  width: cardWidth,
                  height: cardHeight,
                  backgroundColor: COLORS.placeholderBg,
                  border: `1px solid ${COLORS.placeholderBorder}`,
                  borderRadius: `${COLORS.cardBorderRadius}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: `${SPACING.placeholderPadding}px`,
                  fontSize: `${FONT.placeholderSize}px`,
                  color: COLORS.placeholderText,
                  textAlign: 'center',
                  wordBreak: 'break-word',
                }}
              >
                {card.name}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SetView = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const { preferences, updatePreference } = usePreferences();
  const [set, setSet] =  useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [nameSearch, setNameSearch] = useState('');
  const [colorFilter, setColorFilter] = useState([]);
  const [rarityFilter, setRarityFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [viewMode, setViewMode] = useState(preferences.card_display_mode);
  const [inlineSetSearch, setInlineSetSearch] = useState('');

  const handleCardDragStart = useCallback((e, card) => {
    e.dataTransfer.setData(SCRYFALL_ID_DRAG_TYPE, card.id);
    const imageUrl = card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal || '';
    if (imageUrl) {
      e.dataTransfer.setData('text/uri-list', imageUrl);
      e.dataTransfer.setData('text/plain', imageUrl);
    }
    e.dataTransfer.effectAllowed = 'copy';
    e.stopPropagation();
  }, []);

  const searchSets = async (searchTerm) => {
    const controller = new AbortController();
    try {
      const results = await fetch(`${API_BASE}/sets/search/${encodeURIComponent(searchTerm)}`, {
        signal: controller.signal,
      });
      if (!results.ok) {
        throw new Error(`HTTP ${results.status}: Failed to search sets`);
      }
      const data = await results.text();
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      console.error('Set search error:', error);
      throw error;
    }
  };

  const handleSetSelect = (set) => {
    if (set && set.id) {
      navigate(`/set/${set.id}`);
    }
  };

  useEffect(() => {
    setInlineSetSearch('');
  }, [id]);

  useEffect(() => {
    setViewMode(preferences.card_display_mode);
  }, [preferences.card_display_mode]);

  const defaultColumns = [
    {
      key: 'name',
      label: 'Name',
      fixed: true,
      flexGrow: 1,
      sortable: true
    },
    {
      key: 'mana_cost',
      label: 'Mana',
      width: MANA_COST_COLUMN_WIDTH,
      sortable: false
    },
    {
      key: 'type_line',
      label: 'Type',
      flexGrow: 1,
      sortable: true
    },
  ];
  
  const desktopColumns = [
    {
      key: 'rarity',
      label: 'Rarity',
      flexGrow: 1,
      sortable: true
    },
    {
      key: 'colors',
      label: 'Colors',
      width: COLORS_COLUMN_WIDTH,
      sortable: false
    },
    {
      key: 'set_name',
      label: 'Set',
      flexGrow: 1,
      sortable: false 
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const setData  = await fetch(`${API_BASE}/sets/${id}`);
      const setJSON = await setData.json();
      let cards = setJSON.cards;

      setSet(cards);
    } 
     
    fetchData();
  }, [id]);

  const handleSortColumn = (dataKey, newSortType) => {
    if (sortColumn === dataKey) {
      if (sortType === 'asc') {
        setSortType('desc');
      } else if (sortType === 'desc') {
        setSortColumn(null);
        setSortType(null);
      } else {
        setSortType('asc');
      }
    } else {
      setSortColumn(dataKey);
      setSortType('asc');
    }
  };

  const filteredData = useMemo(() => {
    let result = set;

    if (nameSearch) {
      const searchLower = nameSearch.toLowerCase();
      result = result.filter(card => 
        card.name?.toLowerCase().includes(searchLower)
      );
    }

    if (colorFilter.length > 0) {
      result = result.filter(card => {
        const cardColors = card.colors || [];
        const isColorless = cardColors.length === 0;
        const isMulticolor = cardColors.length > 1;

        if (colorFilter.includes('C') && isColorless) return true;
        if (colorFilter.includes('M') && isMulticolor) return true;
        return colorFilter.some(c => c !== 'C' && c !== 'M' && cardColors.includes(c));
      });
    }

    if (rarityFilter.length > 0) {
      result = result.filter(card => 
        rarityFilter.includes(card.rarity?.toLowerCase())
      );
    }

    if (typeFilter.length > 0) {
      result = result.filter(card => 
        typeFilter.some(type => card.type_line?.includes(type))
      );
    }

    return result;
  }, [set, nameSearch, colorFilter, rarityFilter, typeFilter]);

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortType) {
      return filteredData;
    }

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn] || '';
      const bValue = b[sortColumn] || '';

      if (sortColumn === 'rarity') {
        const aRank = RARITY_ORDER[aValue.toLowerCase()] ?? 99;
        const bRank = RARITY_ORDER[bValue.toLowerCase()] ?? 99;
        return sortType === 'asc' ? aRank - bRank : bRank - aRank;
      }

      if (sortType === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  }, [filteredData, sortColumn, sortType]);

  const handleClick = (rowObject) => {
    navigate(`/cardsearch/${rowObject.id}`);
  }

  const handleClearFilters = () => {
    setNameSearch('');
    setColorFilter([]);
    setRarityFilter([]);
    setTypeFilter([]);
    setSortColumn(null);
    setSortType(null);
  };

  const handleViewModeChange = useCallback((value) => {
    setViewMode(value);
    updatePreference('card_display_mode', value);
  }, [updatePreference]);

  const columns = isMobile
    ? defaultColumns
    : [...defaultColumns, ...desktopColumns];

  const wrapperStyles = {
    padding: `${SPACING.contentPadding}px`,
  };

  return (
    <>
    <Container>
        <NavigationBar />
        <Content style={wrapperStyles}>
          <div style={{ marginBottom: `${SPACING.contentPadding}px` }}>
            <SetSearchBar
              term={inlineSetSearch}
              setTerm={setInlineSetSearch}
              retrieve={searchSets}
              onSelect={handleSetSelect}
              placeholder="Search for another set..."
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: `${SPACING.contentPadding}px` }}>
            <div style={{ flex: 1 }}>
              <SetFilters
                nameSearch={nameSearch}
                setNameSearch={setNameSearch}
                colorFilter={colorFilter}
                setColorFilter={setColorFilter}
                rarityFilter={rarityFilter}
                setRarityFilter={setRarityFilter}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                onClearFilters={handleClearFilters}
                totalCards={set.length}
                filteredCount={sortedData.length}
              />
            </div>
            <div style={{ marginLeft: `${SPACING.toggleMargin}px` }}>
              <ViewModeToggle
                value={viewMode}
                onChange={handleViewModeChange}
              />
            </div>
          </div>

          {viewMode === VIEW_MODES.TABLE ? (
            <Table
              loading={set?.length === 0}
              data={sortedData}
              height={window.innerHeight - 100}
              onRowClick={handleClick}
              virtualized
              sortColumn={sortColumn}
              sortType={sortType}
              onSortColumn={handleSortColumn}
            >
              {columns.map(column => {
              const { key, label, ...rest } = column;
              if (key === 'name') {
                return (
                  <Column {...rest} key={key}>
                    <HeaderCell>{label}</HeaderCell>
                    <Cell>
                      {rowData => (
                        <div
                          draggable
                          onDragStart={(e) => handleCardDragStart(e, rowData)}
                          style={{ cursor: 'grab', userSelect: 'none' }}
                        >
                          {rowData.name}
                        </div>
                      )}
                    </Cell>
                  </Column>
                );
              }
              if (key === 'mana_cost') {
                return (
                  <Column {...rest} key={key}>
                    <HeaderCell>{label}</HeaderCell>
                    <Cell>
                      {rowData => {
                        const cost = rowData.mana_cost || rowData.card_faces?.[0]?.mana_cost || '—';
                        return cost;
                      }}
                    </Cell>
                  </Column>
                );
              }
              return (
                <Column {...rest} key={key}>
                  <HeaderCell>{label}</HeaderCell>
                  <Cell dataKey={key} />
                </Column>
              );
            })}
            </Table>
          ) : (
            <GridView
              sortedData={sortedData}
              cardDisplaySize={preferences.card_display_size}
              onCardClick={handleClick}
            />
          )}
        </Content>
    </Container>
    </>
  )
}

export default SetView;
